<template>
  <div v-if="initialLoadComplete && role && roleId">
    <div v-if="matchedTemplateList && serviceAreaId">
      <div v-if="!showTemplateForm && role.securityTemplateId">
        <v-row class="my-2">
          <v-col cols="9"
            ><div>
              <div class="title">
                {{ getTemplate(role.securityTemplateId) || "--" }}
              </div>
              <div class="font-italic">
                (Finalized by
                {{ displayUser(role.securityTemplateFinalizedBy) }} on
                <span class="font-italic">
                  {{ formatDate(role.securityTemplateFinalizedDate) }})
                </span>
              </div>
            </div></v-col
          >
          <v-col cols="3" class="text-right">
            <v-btn
              :disabled="securityQuestionEditIsDisabled"
              @click="showTemplateForm = true"
              data-cy="editTemplateButton"
              class="mt-3"
              color="primary"
              outlined
              >Edit</v-btn
            >
          </v-col>
        </v-row>
      </div>
      <div v-if="showTemplateForm || !role.securityTemplateId">
        <v-card outlined class="my-5">
          <v-card-text>
            <v-row>
              <v-checkbox
                class="ma-0 ml-3 mt-2 mb-3"
                hide-details
                label="Filter to Templates Matching Security Role"
                v-model="showFilteredList"
              ></v-checkbox>
            </v-row>
            <template v-if="showFilteredList">
              <v-row>
                <v-checkbox
                  class="ml-3 mt-0 mb-3"
                  hide-details
                  label="Include Access:None Templates"
                  v-model="showAccessNone"
                ></v-checkbox>
              </v-row>
              <v-row>
                <v-checkbox
                  class="ml-3 mb-3 mt-0"
                  hide-details
                  label="Ignore Groupers"
                  v-model="ignoreGroupers"
                  :disabled="needsIgnoreGroupersRefilter"
                ></v-checkbox>
                <v-chip
                  class="ml-3 mt-3"
                  color="grey"
                  outlined
                  small
                  v-if="needsIgnoreGroupersRefilter"
                >
                  Security Role must be re-saved to enable this option.
                </v-chip>
              </v-row>
            </template>
          </v-card-text>
        </v-card>

        <div class="my-1">
          Currently showing
          <span class="font-weight-bold">{{ matchedTemplateList.length }}</span>
          results.
        </div>
        <v-autocomplete
          clearable
          :filter="customFilter"
          :items="matchedTemplateList"
          :item-text="templateNameFormatted"
          item-color="primary"
          item-value="id"
          outlined
          v-model="securityTemplateId"
        ></v-autocomplete>
        <div class="d-flex justify-end">
          <v-btn
            @click="showTemplateForm = false"
            class="mr-2"
            outlined
            v-if="role.securityTemplateId"
            >Cancel</v-btn
          >
          <v-btn
            :disabled="securityRoleTemplateEditIsDisabled"
            @click="resetSelection"
            class="mr-2"
            outlined
            >Reset Selection</v-btn
          >
          <v-btn
            :disabled="securityRoleTemplateEditIsDisabled"
            @click="assignTemplate"
            class="elevation-0"
            color="primary"
            >Update</v-btn
          >
        </div>
      </div>
    </div>
    <div v-else-if="!serviceAreaId">
      <v-alert dense outlined class="mt-6" color="warning">
        There is no Service Area number for this organization. Security
        Templates cannot be selected until the organization has been assigned a
        Service Area number.
      </v-alert>
    </div>
    <div v-else>
      <v-alert dense outlined class="mt-6" color="warning">
        A saved template configuration could not be found. Please edit the
        security role and re-save it.
      </v-alert>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { cloneDeep, get } from "lodash";
import moment from "moment";

export default {
  computed: {
    buildIsClosed() {
      return this.organizationStatusIsGreaterThanOrEqualTo("buildClosed");
    },
    id() {
      if (this.role) {
        return this.role.id;
      }
      return null;
    },
    needsIgnoreGroupersRefilter() {
      const ignoreGroupersList = get(
        this.role,
        "snapshot.matchedTemplateListIgnoreGroupers"
      );

      return !ignoreGroupersList;
    },
    matchedTemplateList() {
      const { showFilteredList, ignoreGroupers } = this;
      const getString = ignoreGroupers
        ? "snapshot.matchedTemplateListIgnoreGroupers"
        : "snapshot.matchedTemplateList";

      const baseList = this.templateList;
      baseList.sort((a, b) => (a.name > b.name ? 1 : -1));

      let list;
      if (showFilteredList) {
        list = get(this.role, getString);

        if (!this.showAccessNone) {
          list = list.filter(item => !(item.grouper === "ACCESS:NONE"));
        }
      } else {
        list = baseList;
      }

      return list;
    },
    questionAnswerList() {
      return get(this.role, "snapshot.questionAnswerMap");
    },
    securityQuestionEditIsDisabled() {
      return this.buildIsClosed;
    },
    securityRoleTemplateEditIsDisabled() {
      if (this.buildIsClosed) return true;
      if (this.securityTemplateId === this.role.securityTemplateId) return true;
      return false;
    },
    serviceAreaId() {
      return this.organizationRecord.serviceAreaId;
    },
    ...mapGetters({
      getOrganizationRoleById: "secRoleOrganization/getById",
      getTemplateById: "template/getById",
      getUserById: "user/getById",
      organizationStatusIsGreaterThanOrEqualTo:
        "organizationStatus/organizationStatusIsGreaterThanOrEqualTo",
    }),
    ...mapState({
      organizationId: state => state.session.organizationId,
      organizationRecord: state => state.organization.record,
      securityClassList: state => state.securityClass.list,
      templateList: state => state.template.list,
      userId: state => state.session.user.userId,
    }),
  },
  async created() {
    try {
      await Promise.all([
        this.fetchUserList({ reload: false }),
        this.getOrganizationById(this.organizationId),
        this.refreshRole(),
      ]);
      if (this.role) {
        this.securityTemplateId = this.role.securityTemplateId;
      }
      this.initialLoadComplete = true;
    } catch (e) {
      console.error(e);
    }
  },

  data() {
    return {
      ignoreGroupers: false,
      initialLoadComplete: false,
      role: null,
      securityTemplateId: null,
      showAccessNone: true,
      showFilteredList: true,
      showTemplateForm: false,
    };
  },
  methods: {
    async assignTemplate() {
      let securityTemplateId;
      if (!this.securityTemplateId) {
        securityTemplateId = null;
      } else {
        securityTemplateId = this.securityTemplateId;
      }
      try {
        const currentTimeStamp = moment().utc().format("YYYY-MM-DD HH:mm:ss");

        await this.updateOrganizationRole([
          this.id,
          {
            securityTemplateId,
            securityTemplateFinalizedBy: this.userId,
            securityTemplateFinalizedDate: currentTimeStamp,
          },
        ]);
        await this.refreshRole();
        this.notifySuccessRoleUpdate();
        this.showTemplateForm = false;
      } catch (e) {
        console.error(e);
        this.notifyErrorRoleUpdate();
      }
    },
    customFilter(item, queryText) {
      const textId = item.id.toLowerCase();
      const textName = item.name.toLowerCase();
      const searchText = queryText.toLowerCase();

      return (
        textId.indexOf(searchText) > -1 || textName.indexOf(searchText) > -1
      );
    },
    displayUser(id) {
      if (!id) return "--";

      const user = this.getUserById(id);

      if (user) {
        return `${user.firstName} ${user.lastName}`;
      }
      return "OCHIN";
    },
    formatDate(datetime) {
      return moment(datetime).format("LLLL");
    },
    getTemplate(id) {
      if (!id) return;
      const template = this.getTemplateById(id);
      if (template) {
        return `${template.name} (${template.id})`;
      }
    },
    async refreshRole() {
      try {
        await this.fetchRoleList();
      } catch (e) {
        console.error(e);
        this.$notifyError({
          message: "Unable to get list of roles from server",
        });
      }
      const role = this.getOrganizationRoleById(this.roleId);
      if (!role) return;
      this.role = cloneDeep(role);
      if (this.role.snapshot) {
        this.role.snapshot = JSON.parse(this.role.snapshot);
      }
    },
    resetSelection() {
      this.securityTemplateId = this.role.securityTemplateId;
    },
    templateNameFormatted(template) {
      return `${template.name} (${template.id})`;
    },
    ...mapActions({
      fetchRoleList: "secRoleOrganization/list",
      fetchSecurityClassList: "securityClass/list",
      fetchTemplateList: "template/list",
      fetchUserList: "user/list",
      getOrganizationById: "organization/getByOrganizationId",
      updateOrganizationRole: "secRoleOrganization/update",
    }),
  },
  notifications: {
    notifySuccessRoleUpdate: {
      title: "Security Template updated for Organization Role",
      type: "success",
    },
    notifyErrorRoleUpdate: {
      title: "Error Updating Organization Role",
      type: "error",
    },
  },
  props: {
    roleId: {
      type: Number,
    },
  },
};
</script>

<style scoped>
.theme--light.v-chip:not(.v-chip--active) {
  background-color: var(--v-primary-base) !important;
  color: grey !important;
}
</style>
